import { API_BASE_URL } from '@synergy/config';
import { Box, Button, Tabs } from '@mui/material';
import { DownloadIcon } from '@synergy/theme-next';
import { FilterParams, getBonusSettlementTranslationId } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PaloDetailView, PaloSideRail, PaloStatusPill } from '@synergy/components-palo';
import { PoolTab } from '@synergy/components';
import { ReactElement, useEffect, useRef } from 'react';
import { Route, Routes, resolvePath, useLocation, useSearchParams } from 'react-router-dom';
import { noop } from '@synergy/utils';
import { useNavigate, useParams } from 'react-router';
import urlJoin from 'url-join';

import { BonusSettlementDetailTable } from '../components/bonus-settlement-detail-table';
import { BonusSettlementTab } from '../../models/bonus-settlement-tab';
import { PaloBonusSettlementDetailDock } from './palo-bonus-settlement-detail-dock';
import { PoolBonusSettlementPoolTable } from '../components/bonus-settlement-pool-table';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import {
    bonusSettlementDetailSelector,
    bonusSettlementSuppliersSelector,
    poolBonusSettlementDetailSelector,
    poolBonusSettlementSuppliersSelector,
} from '../store/bonus-settlements-detail-selectors';
import {
    customerBonusSettlementDetailsReset,
    customerBonusSettlementSuppliersReset,
    getCustomerBonusSettlementDetail,
    getCustomerBonusSettlementSuppliers,
    getPoolCustomerBonusSettlementCustomers,
    getPoolCustomerBonusSettlementDetail,
} from '../store/bonus-settlements-detail-actions';
import { useAppDispatch, useAppSelector } from '../../../../store/store';
import { useSelector } from '../../../../store/use-appstate-selector';

export const PaloBonusSettlementDetail = (): ReactElement | null => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const currency = useSelector(state => state.currency);
    const isPoolAccount = useSelector(state => state.settings.isPoolAccount);

    const { bonusSettlementSuppliers } = useAppSelector(bonusSettlementSuppliersSelector);
    const { poolBonusSettlementSuppliers } = useAppSelector(poolBonusSettlementSuppliersSelector);
    const { poolBonusSettlementDetails } = useAppSelector(poolBonusSettlementDetailSelector);
    let { bonusSettlementDetails } = useAppSelector(bonusSettlementDetailSelector);

    const { id: bonusSettlementId } = useParams<{ id: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    let selectedTab = BonusSettlementTab.Customer;
    const previousTab = useRef<BonusSettlementTab>(selectedTab);

    if (
        isPoolAccount &&
        bonusSettlementId &&
        resolvePath(ROUTES.bonusSettlements.detail.pool.replace(':id', bonusSettlementId.toString())).pathname ===
            location.pathname
    ) {
        selectedTab = BonusSettlementTab.Pool;
    }

    if (selectedTab === BonusSettlementTab.Pool) {
        bonusSettlementDetails = poolBonusSettlementDetails;
    }

    useEffect(() => {
        if (previousTab.current !== selectedTab) {
            previousTab.current = selectedTab;
            setSearchParams({ [FilterParams.Page]: '1' });
        }
    }, [selectedTab, setSearchParams]);

    useEffect(
        () => () => {
            dispatch(customerBonusSettlementDetailsReset());
            dispatch(customerBonusSettlementSuppliersReset());
        },
        [bonusSettlementId, dispatch]
    );

    useEffect(() => {
        if (bonusSettlementId && !isNaN(+bonusSettlementId)) {
            if (selectedTab === BonusSettlementTab.Pool) {
                dispatch(getPoolCustomerBonusSettlementDetail(+bonusSettlementId));
            } else {
                dispatch(getCustomerBonusSettlementDetail(+bonusSettlementId));
            }
        }
    }, [bonusSettlementId, selectedTab, dispatch]);

    useEffect(() => {
        if (bonusSettlementId && !isNaN(+bonusSettlementId)) {
            if (selectedTab === BonusSettlementTab.Pool) {
                dispatch(getPoolCustomerBonusSettlementCustomers({ id: +bonusSettlementId, searchParams }));
            } else {
                dispatch(getCustomerBonusSettlementSuppliers({ id: +bonusSettlementId, searchParams }));
            }
        }
    }, [bonusSettlementId, selectedTab, searchParams, dispatch]);

    if (!bonusSettlementDetails) {
        return null;
    }

    const exportSettlement = (): void =>
        void window.open(urlJoin(API_BASE_URL, `/bonus-settlements/${bonusSettlementId}/export`), '_self');

    const exportSupplier = (customerId: number): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            window.open(
                urlJoin(
                    API_BASE_URL,
                    `/bonus-settlements/${bonusSettlementId}/pool-customers/${customerId}/export-pdf`
                ),
                '_self'
            );
        }
    };

    const openPoolDetail = (id: number): void => {
        if (bonusSettlementId && +bonusSettlementId) {
            navigate(
                ROUTES.bonusSettlements.poolDetails
                    .replace(':id', bonusSettlementId)
                    .replace(':customerBonusSettlementId', id.toString())
            );
        }
    };

    return (
        <PaloDetailView
            onBack={() => navigate(ROUTES.bonusSettlements.overview)}
            title={
                <FormattedMessage
                    id="app.navigation.bonusSettlements.detail"
                    values={{ year: bonusSettlementDetails.year }}
                />
            }
            status={
                <PaloStatusPill
                    status={bonusSettlementDetails.status}
                    label={<FormattedMessage id={getBonusSettlementTranslationId(bonusSettlementDetails.status)} />}
                />
            }
            actions={
                <Button variant="outlined" color="primary" endIcon={<DownloadIcon />} onClick={exportSettlement}>
                    <FormattedMessage id="app.common.export" />
                </Button>
            }
            tabs={
                isPoolAccount ? (
                    <Tabs indicatorColor="primary" value={selectedTab} sx={{ minHeight: 34 }}>
                        <PoolTab
                            value={BonusSettlementTab.Pool}
                            linkTo={{ pathname: BonusSettlementTab.Pool, search: searchParams.toString() }}
                            label={<FormattedMessage id="app.bonusSettlements.detail.tab.pool" />}
                        />
                        <PoolTab
                            value={BonusSettlementTab.Customer}
                            linkTo={{ pathname: BonusSettlementTab.Customer, search: searchParams.toString() }}
                            label={<FormattedMessage id="app.bonusSettlements.detail.tab.customer" />}
                        />
                    </Tabs>
                ) : undefined
            }
            dock={
                <PaloSideRail
                    sections={[
                        {
                            key: 'details',
                            label: <FormattedMessage id="app.common.details" />,
                            content: (
                                <PaloBonusSettlementDetailDock
                                    currency={currency}
                                    bonusSettlement={bonusSettlementDetails}
                                />
                            ),
                        },
                    ]}
                    collapsedLabel={<FormattedMessage id="app.common.details" />}
                />
            }
        >
            <Box sx={{ height: '100%', overflowY: 'auto' }}>
                <Routes>
                    <Route
                        path={BonusSettlementTab.Customer}
                        element={
                            <BonusSettlementDetailTable
                                currency={currency}
                                requesting={!!bonusSettlementSuppliers}
                                selectable={false}
                                onItemSelected={noop}
                                data={bonusSettlementSuppliers}
                            />
                        }
                    />
                    {isPoolAccount && (
                        <Route
                            path={BonusSettlementTab.Pool}
                            element={
                                <PoolBonusSettlementPoolTable
                                    currency={currency}
                                    requesting={!!poolBonusSettlementSuppliers}
                                    selectable={false}
                                    onItemSelected={openPoolDetail}
                                    data={poolBonusSettlementSuppliers}
                                    onExport={exportSupplier}
                                />
                            }
                        />
                    )}
                </Routes>
            </Box>
        </PaloDetailView>
    );
};
